import React from 'react';
import {
  Drawer,
  Button,
  Typography,
  Grid,
  Box,
  Divider,
  TableRow,
  CircularProgress,
  TableCell,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import { Invoice } from '../../modules/invoice/types';
import {
  StatusTag,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableBody,
  StyledTableCell
} from './styles';

import { pdf } from '@react-pdf/renderer';
import InvoicePDF from './InvoicePDF';

interface InvoiceDetailsModalProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  loading: boolean;
  facilityDetails?: {
    name: string;
    address: string;
  };
}

const InvoiceDetailsModal: React.FC<InvoiceDetailsModalProps> = ({
  open,
  onClose,
  invoice,
  loading,
  facilityDetails,
}) => {
  const [isDownloading, setIsDownloading] = React.useState(false);

  const handleDownload = async (type: 'summary' | 'detailed') => {
    if (!invoice) return;
    setIsDownloading(true);
    try {
      const totalAmount = parseFloat(invoice.invoice_amount || '0');
      const gstPercentage = parseFloat(invoice.gst_applied || '0');
      const subTotal = totalAmount / (1 + gstPercentage / 100);
      const taxAmount = totalAmount - subTotal;

      const blob = await pdf(
        <InvoicePDF
          invoice={invoice}
          subTotal={subTotal}
          taxAmount={taxAmount}
          totalAmount={totalAmount}
          gstPercentage={gstPercentage}
          facilityDetails={facilityDetails}
        />
      ).toBlob();

      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Invoice_${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }, 100);

    } catch (error) {
      console.error('PDF Generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box sx={{ width: { xs: '150vw', sm: 600, md: 700 }, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </Drawer>
    );
  }

  if (!invoice) return null;

  // Calculate totals
  const totalAmount = parseFloat(invoice.invoice_amount || '0');
  const gstPercentage = parseFloat(invoice.gst_applied || '0');
  const subTotal = totalAmount / (1 + gstPercentage / 100);
  const taxAmount = totalAmount - subTotal;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '150vw', sm: 700, md: 800 }, display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Fixed Header */}
        <Box sx={{ p: 4, pb: 2, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              Invoice #{invoice.invoice_number}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Issued on {dayjs(invoice.invoice_date).format('DD/MM/YYYY')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <StatusTag status={invoice.payment_status}>{invoice.payment_status.toUpperCase()}</StatusTag>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>
        </Box>

        {/* Scrollable Content Area - Wrapper for Flex Layout */}
        <Box display="flex" flexDirection="column" sx={{ flexGrow: 1, minHeight: 0 }}>

          {/* Static Top Section (Summary & Title) */}
          <Box sx={{ px: 4, pt: 3, pb: 1, flexShrink: 0 }}>
            {/* Summary Grid */}
            <Grid container spacing={1}>
              <Grid size={{ xs: 12 }}>
                <Typography component="span" variant="subtitle2" color="textSecondary" sx={{ mr: 1 }}>
                  Period Covered:
                </Typography>
                <Typography component="span" variant="body1" fontWeight="medium">
                  {dayjs(invoice.period_start_from).format('DD/MM/YYYY')} - {dayjs(invoice.period_end_to).format('DD/MM/YYYY')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography component="span" variant="subtitle2" color="textSecondary" sx={{ mr: 1 }}>
                  Due Date:
                </Typography>
                <Typography component="span" variant="body1" fontWeight="medium">
                  {dayjs(invoice.invoice_due_date).format('DD/MM/YYYY')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography component="span" variant="subtitle2" color="textSecondary" sx={{ mr: 1 }}>
                  Total Amount:
                </Typography>
                <Typography component="span" variant="body1" fontWeight="medium">
                  ${totalAmount.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ mt: 3, mb: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Invoice Details
            </Typography>
          </Box>

          {/* Scrollable Table Area */}
          <Box sx={{ flexGrow: 1, px: 4, pb: 2, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <StyledTableContainer sx={{ boxShadow: 'none', border: 'none', mb: 0, flexGrow: 1, overflow: 'auto', height: '100%' }}>
              <StyledTable size="small" stickyHeader>
                <StyledTableHead>
                  <TableRow>
                    <TableCell>Shift ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Worker</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Hours</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </StyledTableHead>
                <StyledTableBody>
                  {invoice.facility_invoice_items?.map((item: any) => (
                    <React.Fragment key={item.invoice_item_id}>
                      {item.payment_facility_invoice?.map((payment: any, idx: number) => (
                        <TableRow key={`${item.invoice_item_id}-${idx}`} hover>
                          <StyledTableCell>{item.shift.shift_id}</StyledTableCell>
                          <StyledTableCell>{dayjs(item.shift.start_date_time).format('DD/MM/YYYY')}</StyledTableCell>
                          <StyledTableCell>
                            <Typography variant="body2" fontWeight="medium">{payment.worker.user.first_name} {payment.worker.user.last_name}</Typography>
                          </StyledTableCell>
                          <StyledTableCell>{item.shift.position_role}</StyledTableCell>
                          <StyledTableCell>{payment.hours_worked}h</StyledTableCell>
                          <StyledTableCell align="right">${payment.amount}</StyledTableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </StyledTableBody>
              </StyledTable>
            </StyledTableContainer>
          </Box>

          {/* Static Bottom Section (Payment Summary & Actions) */}
          <Box sx={{ p: 4, pt: 2, borderTop: '1px solid #eee', backgroundColor: '#fff', flexShrink: 0 }}>
            {/* Payment Summary */}
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography color="textSecondary">Subtotal</Typography>
                  <Typography fontWeight="medium">${subTotal.toFixed(2)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography color="textSecondary">GST ({gstPercentage.toFixed(2)}%)</Typography>
                  <Typography fontWeight="medium">${taxAmount.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="bold">Total</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">${totalAmount.toFixed(2)}</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload('summary')}
                disabled={isDownloading}
              >
                {isDownloading ? 'Exporting...' : 'Export Invoice'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default InvoiceDetailsModal;
